"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, useSyncExternalStore } from "react";

import { bookingOverlayOpen, subscribeBookingOverlay } from "@/lib/booking-overlay-state";
import { COOKIE_CONSENT_CHANGE_EVENT, readCookieConsent } from "@/lib/cookie-consent";
import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import type {
  AdsPopupCopy,
  AiVisibilityPopupCopy,
  AutomationPopupCopy,
  LeadGenPopupCopy,
  SiteCheckPopupCopy,
} from "@/lib/conversion-popup-copy";
import type { Lang } from "@/lib/types";

// Each offer's panel only loads once a trigger fires.
const CcSiteCheckPanel = dynamic(() => import("./CcSiteCheckPanel").then((module) => module.CcSiteCheckPanel), { ssr: false });
const CcAiVisibilityPanel = dynamic(() => import("./CcAiVisibilityPanel").then((module) => module.CcAiVisibilityPanel), { ssr: false });
const CcAdsCalculatorPanel = dynamic(() => import("./CcAdsCalculatorPanel").then((module) => module.CcAdsCalculatorPanel), { ssr: false });
const CcLeadGenCalculatorPanel = dynamic(() => import("./CcLeadGenCalculatorPanel").then((module) => module.CcLeadGenCalculatorPanel), { ssr: false });
const CcAutomationQuizPanel = dynamic(() => import("./CcAutomationQuizPanel").then((module) => module.CcAutomationQuizPanel), { ssr: false });

/** What a popup shows: the offer and the data its panel needs. */
export type ConversionPanelConfig =
  | { offer: "seo" | "web"; copy: SiteCheckPopupCopy }
  | { offer: "aiVisibility"; copy: AiVisibilityPopupCopy; freemailDomains: string[] }
  | { offer: "ads"; copy: AdsPopupCopy }
  | { offer: "leadGen"; copy: LeadGenPopupCopy }
  | { offer: "automation"; copy: AutomationPopupCopy; links: Record<string, string> };

const SEEN_KEY = "cc-seo-popup-seen";
const DISMISSED_KEY = "cc-seo-popup-dismissed";
const DISMISS_DAYS = 7;
const READ_DEPTH = 1 / 3;
const MOBILE_READ_DEPTH = 1 / 3;
const EXIT_INTENT_AFTER_SECONDS = 8;

function subscribeConsent(callback: () => void) {
  window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}
const hasConsent = () => readCookieConsent() !== null;

// Another bottom-corner panel (the language suggestion) gets to finish first.
const OTHER_PANEL_SELECTOR = ".cc-language-suggestion";
function subscribeOtherPanel(callback: () => void) {
  const observer = new MutationObserver(callback);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}
const otherPanelOpen = () => document.querySelector(OTHER_PANEL_SELECTOR) !== null;
const serverFalse = () => false;

function storageGet(storage: "local" | "session", key: string): string | null {
  try {
    return (storage === "local" ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}
function storageSet(storage: "local" | "session", key: string, value: string) {
  try {
    (storage === "local" ? window.localStorage : window.sessionStorage).setItem(key, value);
  } catch {
    // Private mode or blocked storage: the popup simply may show again.
  }
}

/** Shown at most once per visit, and not again for a week after being closed. */
function mayShow(): boolean {
  if (storageGet("session", SEEN_KEY)) return false;
  const dismissed = Number(storageGet("local", DISMISSED_KEY) ?? 0);
  return !dismissed || Date.now() - dismissed > DISMISS_DAYS * 86_400_000;
}

const readDepth = () => {
  const max = document.documentElement.scrollHeight - window.innerHeight;
  return max > 0 ? window.scrollY / max : 0;
};

type CcConversionPopupProps = {
  panel: ConversionPanelConfig;
  lang: Lang;
  /** Booking copy the CTA opens (the page's own variant). */
  bookingVariant: BookCallPopupVariant;
  /** Seconds on the page before it may appear (with a third of the page read). */
  delaySeconds: number;
};

/**
 * Behaviour-triggered conversion popup (one offer per page). Appears when a visitor
 * has read most of the page for a while, on exit intent (desktop), or on a
 * fast scroll back up after reading (phones). Never before the cookie choice,
 * never over the booking popup or the language suggestion. `?popup=1` shows it right away for review.
 */
export function CcConversionPopup({ panel, lang, bookingVariant, delaySeconds }: CcConversionPopupProps) {
  const consentReady = useSyncExternalStore(subscribeConsent, hasConsent, serverFalse);
  const bookingOpen = useSyncExternalStore(subscribeBookingOverlay, bookingOverlayOpen, serverFalse);
  const otherOpen = useSyncExternalStore(subscribeOtherPanel, otherPanelOpen, serverFalse);
  const [triggered, setTriggered] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (triggered) return;
    const forced = new URLSearchParams(window.location.search).get("popup") === "1";
    if (forced) {
      const timer = window.setTimeout(() => setTriggered(true), 600);
      return () => window.clearTimeout(timer);
    }
    if (!mayShow()) return;

    const started = Date.now();
    const seconds = () => (Date.now() - started) / 1000;
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    let lastY = window.scrollY;
    let lastT = performance.now();
    let deepestRead = 0;

    const fire = () => setTriggered(true);
    const onScroll = () => {
      const depth = readDepth();
      deepestRead = Math.max(deepestRead, depth);
      if (depth >= READ_DEPTH && seconds() >= delaySeconds) fire();
      if (!finePointer) {
        const now = performance.now();
        const speed = (lastY - window.scrollY) / Math.max(now - lastT, 1);
        // A quick flick back up after reading usually means "leaving".
        if (speed > 2.2 && deepestRead >= MOBILE_READ_DEPTH && seconds() >= delaySeconds / 2) fire();
        lastY = window.scrollY;
        lastT = now;
      }
    };
    const onLeave = (event: MouseEvent) => {
      if (!event.relatedTarget && event.clientY <= 0 && seconds() >= EXIT_INTENT_AFTER_SECONDS) fire();
    };
    // Also covers visitors who read without scrolling further after the time limit.
    const timer = window.setInterval(onScroll, 4000);

    window.addEventListener("scroll", onScroll, { passive: true });
    if (finePointer) document.addEventListener("mouseout", onLeave);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onLeave);
    };
  }, [triggered, delaySeconds]);

  useEffect(() => {
    if (triggered) storageSet("session", SEEN_KEY, "1");
  }, [triggered]);

  if (!triggered || closed || !consentReady || bookingOpen || otherOpen) return null;

  const onClose = () => {
    storageSet("local", DISMISSED_KEY, String(Date.now()));
    setClosed(true);
  };
  const shared = { lang, bookingVariant, onClose };
  switch (panel.offer) {
    case "seo":
    case "web":
      return <CcSiteCheckPanel copy={panel.copy} profile={panel.offer} {...shared} />;
    case "aiVisibility":
      return <CcAiVisibilityPanel copy={panel.copy} freemailDomains={panel.freemailDomains} {...shared} />;
    case "ads":
      return <CcAdsCalculatorPanel copy={panel.copy} {...shared} />;
    case "leadGen":
      return <CcLeadGenCalculatorPanel copy={panel.copy} {...shared} />;
    case "automation":
      return <CcAutomationQuizPanel copy={panel.copy} links={panel.links} {...shared} />;
  }
}
